import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import FavoritesApi from 'src/api/favorites';
import { FavoriteTarget, UserFavorite } from 'src/api/types/favorite';
import { errorToast } from 'src/utils/toasts';

interface OptimisticFavoriteTarget extends FavoriteTarget {
  label?: string;
  path?: string;
  target?: UserFavorite['target'];
}

interface FavoritesContextValue {
  favorites: UserFavorite[];
  loading: boolean;
  refreshFavorites: () => Promise<void>;
  getFavorite: (target: FavoriteTarget) => UserFavorite | undefined;
  addFavorite: (target: OptimisticFavoriteTarget) => Promise<UserFavorite | undefined>;
  removeFavorite: (favoriteId: string) => Promise<void>;
  updateFavorite: (
    favoriteId: string,
    data: { includeChildren: boolean }
  ) => Promise<UserFavorite | undefined>;
  reorderFavorites: (favoriteIds: string[]) => Promise<void>;
}

const FavoritesContext = createContext<FavoritesContextValue | null>(null);

const favoriteKey = (target: FavoriteTarget) => `${target.targetType}:${target.targetId}`;

const sortFavorites = (favorites: UserFavorite[]) =>
  [...favorites].sort((a, b) => a.position - b.position || a.createdAt.localeCompare(b.createdAt));

const makeOptimisticFavorite = (
  target: OptimisticFavoriteTarget,
  position: number
): UserFavorite => {
  const now = new Date().toISOString();
  return {
    id: `pending-${favoriteKey(target)}`,
    targetType: target.targetType,
    targetId: target.targetId,
    label: target.label ?? 'Favorite',
    path: target.path ?? window.location.pathname,
    position,
    includeChildren: target.includeChildren ?? false,
    createdAt: now,
    updatedAt: now,
    target: target.target ?? {}
  };
};

export const FavoritesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [favorites, setFavorites] = useState<UserFavorite[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshFavorites = useCallback(async () => {
    try {
      setLoading(true);
      const response = await FavoritesApi.fetchFavorites();
      setFavorites(sortFavorites(response.data.favorites));
    } catch {
      errorToast({
        title: 'Favorites did not load',
        description: 'Try refreshing the page.'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshFavorites();
  }, [refreshFavorites]);

  const getFavorite = useCallback(
    (target: FavoriteTarget) =>
      favorites.find((favorite) => favoriteKey(favorite) === favoriteKey(target)),
    [favorites]
  );

  const addFavorite = useCallback(
    async (target: OptimisticFavoriteTarget) => {
      const existing = favorites.find((favorite) => favoriteKey(favorite) === favoriteKey(target));
      if (existing) return existing;

      const previousFavorites = favorites;
      const optimisticFavorite = makeOptimisticFavorite(target, favorites.length);
      setFavorites(sortFavorites([...favorites, optimisticFavorite]));

      try {
        const response = await FavoritesApi.createFavorite({
          targetType: target.targetType,
          targetId: target.targetId,
          includeChildren: target.includeChildren
        });
        setFavorites((current) =>
          sortFavorites(
            current.map((favorite) =>
              favorite.id === optimisticFavorite.id ? response.data.favorite : favorite
            )
          )
        );
        return response.data.favorite;
      } catch {
        setFavorites(previousFavorites);
        errorToast({
          title: 'Favorite was not saved',
          description: 'Please try again.'
        });
        return undefined;
      }
    },
    [favorites]
  );

  const removeFavorite = useCallback(
    async (favoriteId: string) => {
      const previousFavorites = favorites;
      setFavorites(favorites.filter((favorite) => favorite.id !== favoriteId));

      try {
        await FavoritesApi.deleteFavorite(favoriteId);
      } catch {
        setFavorites(previousFavorites);
        errorToast({
          title: 'Favorite was not removed',
          description: 'Please try again.'
        });
      }
    },
    [favorites]
  );

  const updateFavorite = useCallback(
    async (favoriteId: string, data: { includeChildren: boolean }) => {
      const previousFavorites = favorites;
      setFavorites(
        favorites.map((favorite) =>
          favorite.id === favoriteId ? { ...favorite, ...data } : favorite
        )
      );

      try {
        const response = await FavoritesApi.updateFavorite(favoriteId, data);
        setFavorites((current) =>
          sortFavorites(
            current.map((favorite) =>
              favorite.id === favoriteId ? response.data.favorite : favorite
            )
          )
        );
        return response.data.favorite;
      } catch {
        setFavorites(previousFavorites);
        errorToast({
          title: 'Favorite was not updated',
          description: 'Please try again.'
        });
        return undefined;
      }
    },
    [favorites]
  );

  const reorderFavorites = useCallback(
    async (favoriteIds: string[]) => {
      const previousFavorites = favorites;
      const favoriteById = new Map(favorites.map((favorite) => [favorite.id, favorite]));
      const nextFavorites = favoriteIds
        .map((favoriteId, position) => {
          const favorite = favoriteById.get(favoriteId);
          return favorite ? { ...favorite, position } : null;
        })
        .filter((favorite): favorite is UserFavorite => Boolean(favorite));
      setFavorites(sortFavorites(nextFavorites));

      try {
        const response = await FavoritesApi.reorderFavorites(favoriteIds);
        setFavorites(sortFavorites(response.data.favorites));
      } catch {
        setFavorites(previousFavorites);
        errorToast({
          title: 'Favorite order was not saved',
          description: 'Please try again.'
        });
      }
    },
    [favorites]
  );

  const value = useMemo(
    () => ({
      favorites,
      loading,
      refreshFavorites,
      getFavorite,
      addFavorite,
      removeFavorite,
      updateFavorite,
      reorderFavorites
    }),
    [
      favorites,
      loading,
      refreshFavorites,
      getFavorite,
      addFavorite,
      removeFavorite,
      updateFavorite,
      reorderFavorites
    ]
  );

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within FavoritesProvider');
  }
  return context;
};
